import React, { forwardRef, useId } from 'react';
import { ChevronDown, EyeOff } from 'lucide-react';
import {
  AppBadge, AppCard, Callout, Checkbox, cn,
} from '../ui';

// ─── PageSectionCard — one section of a website page, in the Pages tab ───
//
// A disclosure: the header is a real button (Enter and Space, aria-expanded),
// and the section's show/hide checkbox sits beside it rather than inside it,
// because an interactive control nested in a button is unreachable.
//
// Props:
//   title, hint
//   filled / total — how many of its fields hold the admin's own content
//   expanded, onToggle
//   visibility — { shown, onChange(bool), hiddenNote? } when the website
//                lets this section be hidden, otherwise omitted. `hiddenNote`
//                replaces the "this section is hidden" notice — an opt-in
//                section was never shown, so "hidden" is the wrong word.
//   actions    — extra controls for the section's header (an AI button)

const PageSectionCard = forwardRef(({
  title, hint, filled = 0, total = 0, expanded, onToggle, visibility, actions, children,
}, ref) => {
  const bodyId = useId();
  const shown = visibility ? visibility.shown : true;

  return (
    <AppCard ref={ref} as="section" aria-label={title} className="tw-scroll-mt-24">
      <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-x-4 tw-gap-y-2 tw-px-5 tw-py-4">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggle}
          className="tw-flex tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-sm tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-focus-ring"
        >
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={cn('tw-mt-0.5 tw-shrink-0 tw-text-content-subtle tw-transition-transform', !expanded && '-tw-rotate-90')}
          />
          <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
            <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <span className="tw-text-[0.9375rem] tw-font-semibold tw-text-content">{title}</span>
              {total > 0 && (
                <AppBadge size="sm" variant={filled > 0 ? 'secondary' : 'outline'}>
                  {filled > 0 ? `${filled} of ${total} filled` : 'Sample content'}
                </AppBadge>
              )}
              {!shown && (
                <AppBadge size="sm" variant="outline"><EyeOff size={12} aria-hidden="true" /> Hidden</AppBadge>
              )}
            </span>
            {hint && <span className="tw-text-sm tw-leading-snug tw-text-content-muted">{hint}</span>}
          </span>
        </button>
        {(visibility || actions) && (
          <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3">
            {actions}
            {visibility && (
              <Checkbox
                size="sm"
                label="Show on website"
                checked={visibility.shown}
                onChange={(e) => visibility.onChange(e.target.checked)}
              />
            )}
          </div>
        )}
      </div>

      {expanded && (
        <div id={bodyId} className="tw-flex tw-flex-col tw-gap-4 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-p-5">
          {!shown && (
            <Callout tone="info">
              {visibility?.hiddenNote
                || 'This section is hidden on your website. What you enter here is kept for when you show it again.'}
            </Callout>
          )}
          {children}
        </div>
      )}
    </AppCard>
  );
});
PageSectionCard.displayName = 'PageSectionCard';

export default PageSectionCard;
